import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as cbor from 'cbor-web';
import { Buffer } from 'buffer';

// Configure Buffer for the browser environment
window.Buffer = window.Buffer || Buffer;

const environment = {
  apiUrl: 'https://verifier.veritage.io/ui/presentations'
};

const AGE_ATTESTATION = 18; // Hardcoded to 18

// Helper function to process vp_token and verify age
const processVpToken = async (vpToken, requiredAge, navigate) => {
  try {
    console.log('Raw vp_token:', vpToken);
    
    // Decode CBOR token using Buffer and proper options
    const buf = Buffer.from(vpToken, 'base64');
    
    // Decode the CBOR data
    const valueOut = await cbor.decodeFirst(buf, {
      bigint: true,
      preferWeb: true
    });
    
    console.log('Decoded CBOR token:', valueOut);
    
    // Extract the namespaces and data according to the sample code approach
    if (valueOut.documents && valueOut.documents[0] && valueOut.documents[0].issuerSigned) {
      const namespaces = valueOut.documents[0].issuerSigned.nameSpaces;
      const namespaceKeys = Object.keys(namespaces);
      
      if (namespaceKeys.length > 0) {
        const firstNamespace = namespaceKeys[0];
        const dataArray = namespaces[firstNamespace];
        
        console.log('First namespace:', firstNamespace);
        console.log('Data array:', dataArray);
        
        // Process each item in the data array
        const extractedData = [];
        
        for (const item of dataArray) {
          if (item.value) {
            try {
              // Decode the inner CBOR data
              const decodedItem = await cbor.decodeFirst(item.value, {
                bigint: true,
                preferWeb: true
              });
              
              console.log('Decoded item:', decodedItem);
              
              if (decodedItem.elementIdentifier && decodedItem.elementValue) {
                extractedData.push({
                  key: decodedItem.elementIdentifier.replaceAll('_', ' '),
                  value: decodedItem.elementValue
                });
              }
            } catch (itemError) {
              console.error('Error decoding item:', itemError);
            }
          }
        }
        
        console.log('Extracted data:', extractedData);
        
        // Update UI based on extracted data
        if (extractedData.length > 0) {
          // Check if age verification passes based on birth date
          const birthDateItem = extractedData.find(item => item.key.includes('birth date'));
          if (birthDateItem) {
            console.log(`Found birth date: ${birthDateItem.value}`);
            
            // Extract the date string from CBOR tag if needed
            let birthDateStr;
            
            if (typeof birthDateItem.value === 'object' && birthDateItem.value !== null) {
              // Handle CBOR tagged value (e.g., 1004("1992-08-08"))
              if (birthDateItem.value.value && typeof birthDateItem.value.value === 'string') {
                birthDateStr = birthDateItem.value.value;
              } else if (birthDateItem.value.toString) {
                // Try to extract date from the string representation
                const tagStr = birthDateItem.value.toString();
                const dateMatch = tagStr.match(/"([0-9-]+)"/);
                if (dateMatch && dateMatch[1]) {
                  birthDateStr = dateMatch[1];
                }
              }
              console.log(`Extracted date string from CBOR tag: ${birthDateStr}`);
            } else if (typeof birthDateItem.value === 'string') {
              // Direct string value
              birthDateStr = birthDateItem.value;
            }
            
            if (!birthDateStr) {
              console.error('Could not extract valid birth date string');
              navigate('/age-verified', {
                state: {
                  isDigitalId: true,
                  isVerified: false,
                  requiredAge: requiredAge
                }
              });
              return false;
            }
            
            // Calculate age based on birth date
            const birthDate = new Date(birthDateStr);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            
            console.log(`Calculated age: ${age}, Required age: ${requiredAge}`);
            
            if (age >= requiredAge) {
              console.log('Age verification passed!');
              // Navigate to result screen
              navigate('/age-verified', {
                state: {
                  isDigitalId: true,
                  isVerified: true,
                  age: age
                }
              });
              return true;
            } else {
              console.log('Age verification failed - underage');
              // Navigate to result screen with failure
              navigate('/age-verified', {
                state: {
                  isDigitalId: true,
                  isVerified: false,
                  age: age,
                  requiredAge: requiredAge
                }
              });
              return false;
            }
          }
        }
      } else {
        console.warn('No namespaces found in the token');
      }
    } else {
      console.warn('Token does not contain the expected structure');
    }
  } catch (error) {
    console.error('Error decoding CBOR:', error);
    // Try fallback approach with direct parsing
    try {
      if (vpToken.includes('birth_date')) {
        console.log('Attempting fallback parsing of birth date');
        // Simple fallback to detect if it appears to be an adult
        if (vpToken.includes('1990') || 
            vpToken.includes('1980') || 
            vpToken.includes('1970')) {
          console.log('Fallback detected adult birth year');
          navigate('/age-verified', {
            state: {
              isDigitalId: true,
              isVerified: true
            }
          });
          return true;
        }
      }
    } catch (fallbackError) {
      console.error('Fallback parsing failed:', fallbackError);
    }
  }
  
  // Default to failed if we can't process
  navigate('/age-verified', {
    state: {
      isDigitalId: true,
      isVerified: false
    }
  });
  return false;
};

function DigitalIdCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const responseCode = searchParams.get('response_code');

    if (!responseCode) {
      console.error('No response_code found in URL');
      // Navigate back to digital ID if no response code
      setTimeout(() => {
        navigate('/digital-id');
      }, 2000);
      return;
    }

    const storedPresentationStr = localStorage.getItem('presentation');
    if (!storedPresentationStr) {
      console.error('No presentation found in local storage');
      // Navigate back to digital ID if no presentation
      setTimeout(() => {
        navigate('/digital-id');
      }, 2000);
      return;
    }

    const storedPresentation = JSON.parse(storedPresentationStr);
    const presentationId = storedPresentation.presentation_id;
    const storedAgeAttestation = AGE_ATTESTATION;
    
    console.log(`Retrieving verification with age attestation: ${storedAgeAttestation}`);
    
    const getRequestUrl = `${environment.apiUrl}/${presentationId}?response_code=${responseCode}`;
    
    const fetchPresentation = async () => {
      try {
        const response = await fetch(getRequestUrl);
        if (response.ok) {
          const result = await response.json();
          console.log('Verification result:', result);
          
          // Check if vp_token exists in the response
          if (result.vp_token) {
            await processVpToken(result.vp_token, storedAgeAttestation, navigate);
          } else {
            console.warn('No vp_token found in the response');
            // Navigate to digital ID if no token
            navigate('/digital-id');
          }
        } else {
          console.error('Failed to fetch presentation', response.statusText);
          navigate('/digital-id');
        }
      } catch (error) {
        console.error('Error fetching presentation', error);
        navigate('/digital-id');
      }
    };

    fetchPresentation();
  }, [navigate, searchParams]);

  // Show a loading state while processing
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e0e0e0',
          borderTopColor: '#424242',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{
          fontSize: '16px',
          color: '#333333',
          margin: 0
        }}>Processing verification...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default DigitalIdCallback;
